import { useQuery } from '@tanstack/react-query';
import type { components } from '@trackercop/contracts';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-keys';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { ReviewPopover } from '@/components/review-popover';

type ScanFinding = components['schemas']['ScanFinding'];
type ReviewMap = Map<string, components['schemas']['FindingReview']>;

interface FindingsTabProps {
  crawlId: string;
  reviews: ReviewMap;
}

export function FindingsTab({ crawlId, reviews }: FindingsTabProps) {
  // The compliance report is the aggregated finding stream; merge all
  // three sections into one table so reviewers can process findings
  // regardless of which framework flagged them.
  const query = useQuery({
    queryKey: queryKeys.crawls.report(crawlId),
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/crawls/{crawlId}/report', {
        params: { path: { crawlId } },
      });
      if (error) throw error;
      return data;
    },
  });

  if (query.isLoading) return <p className="text-sm text-muted-foreground">Loading findings…</p>;
  if (query.isError || !query.data) return <p className="text-sm text-red-600">Failed to load findings.</p>;

  const findings: Array<ScanFinding & { framework: string }> = [
    ...query.data.vppa.findings.map((f) => ({ ...f, framework: 'VPPA' })),
    ...query.data.gdpr.findings.map((f) => ({ ...f, framework: 'GDPR' })),
    ...query.data.ccpa.findings.map((f) => ({ ...f, framework: 'CCPA' })),
  ];

  return (
    <Table>
      <THead>
        <TR>
          <TH>Framework</TH>
          <TH>Severity</TH>
          <TH>ID</TH>
          <TH>Message</TH>
          <TH>Entity</TH>
          <TH>Review</TH>
        </TR>
      </THead>
      <TBody>
        {findings.length === 0 ? (
          <TR><TD colSpan={6} className="text-center text-xs text-muted-foreground">No findings.</TD></TR>
        ) : (
          findings.map((f, idx) => {
            const reviewKey = f.entityType && f.entityId ? `${f.entityType}:${f.entityId}` : `finding:${f.id}`;
            const review = reviews.get(reviewKey);
            return (
              <TR key={`${f.id}-${idx}`}>
                <TD><Badge variant="muted">{f.framework}</Badge></TD>
                <TD>
                  <Badge
                    variant={
                      f.severity === 'critical' || f.severity === 'error'
                        ? 'destructive'
                        : f.severity === 'warn'
                          ? 'warning'
                          : 'info'
                    }
                  >
                    {f.severity}
                  </Badge>
                </TD>
                <TD className="font-mono text-xs">{f.id}</TD>
                <TD>{f.message}</TD>
                <TD className="text-xs text-muted-foreground">
                  {f.entityType ? `${f.entityType}:${f.entityId?.slice(0, 12)}` : '—'}
                </TD>
                <TD>
                  {f.entityType && f.entityId ? (
                    <ReviewPopover
                      crawlId={crawlId}
                      entityType={f.entityType}
                      entityId={f.entityId}
                      currentStatus={review?.status ?? 'unreviewed'}
                      currentNote={review?.note ?? null}
                    />
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </TD>
              </TR>
            );
          })
        )}
      </TBody>
    </Table>
  );
}
